import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axiosInstance from '../../Services/axiosInstance';
import LoadingSpinner from '../common/LoadingSpinner';
import { useToaster } from '../common/Toaster';
import {
  UserApiResponse,
  isCreateMigrationWizardResponse,
  isAxiosError,
  getApiErrorDetails
} from '../../types/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../ui/pagination';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { X, Copy, Search, ArrowRightLeft, Trash2, Check, UserX, Pencil, Loader2 } from 'lucide-react';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string | null;
  workspaceId: string | null;
  organization: string | null;
  subscriptionId: string | null;
}

interface SubscriptionOption {
  label: string;
  value: string;
}

interface UsersTableProps {
  refreshKey?: number;
}

const AVAILABLE_ROLES = ['Administrator', 'User'];

const isValidRedirectUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const UsersTable: React.FC<UsersTableProps> = ({ refreshKey = 0 }) => {
  const { notify } = useToaster();
  const [data, setData] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [roleChangeUser, setRoleChangeUser] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [isRoleChanging, setIsRoleChanging] = useState(false);
  const [subscriptionOptions, setSubscriptionOptions] = useState<SubscriptionOption[]>([]);
  const [selectedSubscription, setSelectedSubscription] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let hasNotified = false;
    let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const fetchUsers = async (retryCount = 0): Promise<void> => {
      const maxRetries = 3;

      try {
        const response = await axiosInstance.get<UserApiResponse[]>('/api/UserMigration/users');

        if (!isMounted) return;

        const users: User[] = response.data.map((item: UserApiResponse) => ({
          id: item.id,
          firstName: item.firstName,
          lastName: item.lastName,
          email: item.email,
          role: item.role || null,
          workspaceId: item.workspaceId || null,
          organization: item.applicationSubscription?.organisation?.name || null,
          subscriptionId: item.applicationSubscription?.id || null,
        }));

        setData(users);

        const uniqueSubscriptions = Array.from(
          new Set(
            users
              .filter(user => user.subscriptionId && user.organization)
              .map(user => JSON.stringify({ subscriptionId: user.subscriptionId, organization: user.organization })),
          ),
        ).map((subscription: string) => {
          const { subscriptionId, organization } = JSON.parse(subscription);
          return {
            label: organization,
            value: subscriptionId,
          };
        });

        setSubscriptionOptions(uniqueSubscriptions);

        if (uniqueSubscriptions.length > 0) {
          setSelectedSubscription(uniqueSubscriptions[0].value);
        }
      } catch (error: unknown) {
        console.error('[UsersTable] Error fetching users:', error);

        if (!isMounted) return;

        // Check if error is retryable
        const isRetryable = isAxiosError(error) && (
          error.code === 'ECONNABORTED' ||
          error.code === 'ETIMEDOUT' ||
          (error.response?.status && error.response.status >= 500)
        );

        if (isRetryable && retryCount < maxRetries) {
          const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
          retryTimeoutId = setTimeout(() => {
            if (isMounted) {
              fetchUsers(retryCount + 1);
            }
          }, delay);
          return;
        }

        if (!hasNotified && isMounted) {
          hasNotified = true;
          const errorDetails = getApiErrorDetails(error);
          notify('Error', errorDetails.message, 'danger');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUsers();

    return () => {
      isMounted = false;
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
      }
    };
  }, [notify, refreshKey]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedQuery]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only clear search if no dialog is open to avoid conflicts with dialog dismiss
      if (e.key === 'Escape' && !isModalOpen && !isDeleteDialogOpen && !isRoleDialogOpen) {
        setQuery('');
        setDebouncedQuery('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, isDeleteDialogOpen, isRoleDialogOpen]);

  const handleCopyId = useCallback(async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      notify('Copied', 'ID copied to clipboard', 'success');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      notify('Error', 'Failed to copy ID', 'danger');
    }
  }, [notify]);

  const handleClearSearch = useCallback(() => {
    setQuery('');
    setDebouncedQuery('');
  }, []);

  const truncateId = useCallback((id: string) => {
    return id.length > 12 ? `${id.substring(0, 8)}...${id.slice(-4)}` : id;
  }, []);

  const handleMigrate = (user: User) => {
    setSelectedUser(user);
    setIsModalOpen(true);
    if (subscriptionOptions.length > 0) {
      setSelectedSubscription(subscriptionOptions[0].value);
    }
  };

  const handleMigrateConfirm = async () => {
    if (!selectedUser || !selectedSubscription) {
      notify('Validation', 'User or subscription not selected', 'warning');
      return;
    }

    const payload = {
      subscriptionId: selectedSubscription,
      firstName: selectedUser.firstName,
      lastName: selectedUser.lastName,
      email: selectedUser.email,
      SourceUserId: selectedUser.id,
    };

    try {
      const response = await axiosInstance.post('/api/UserMigration/CreateUserMigrationWizardUrl', payload);

      // Validate response structure
      if (!isCreateMigrationWizardResponse(response.data)) {
        notify('Error', 'Invalid response from server', 'danger');
        return;
      }

      const { userMigrationWizardUrl } = response.data.data.createUserMigrationWizardUrl;

      if (!isValidRedirectUrl(userMigrationWizardUrl)) {
        notify('Error', 'Invalid migration wizard URL received', 'danger');
        return;
      }
      setIsModalOpen(false);
      notify('Migration', 'Wizard URL generated, redirecting...', 'success');
      window.location.href = userMigrationWizardUrl;
    } catch (error: unknown) {
      const errorDetails = getApiErrorDetails(error);
      notify('Error', errorDetails.message || 'Failed to migrate user', 'danger');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await axiosInstance.delete(`/api/UserMigration/users/${id}`);
      setData(prev => prev.filter(user => user.id !== id));
      notify('Deleted', 'User deleted', 'success');
    } catch (error: unknown) {
      const errorDetails = getApiErrorDetails(error);
      notify('Error', errorDetails.message || 'Failed to delete user', 'danger');
    }
  };

  const handleOpenRoleDialog = (user: User) => {
    setRoleChangeUser(user);
    setSelectedRole(user.role || 'User');
    setIsRoleDialogOpen(true);
  };

  const handleRoleChangeConfirm = async () => {
    if (!roleChangeUser || !selectedRole) return;

    setIsRoleChanging(true);
    try {
      await axiosInstance.put(`/api/UserMigration/users/${roleChangeUser.id}/role`, {
        roleName: selectedRole,
      });

      setData(prev =>
        prev.map(u => u.id === roleChangeUser.id ? { ...u, role: selectedRole } : u)
      );
      notify('Success', `Role changed to ${selectedRole}`, 'success');
      setIsRoleDialogOpen(false);
      setRoleChangeUser(null);
    } catch (error: unknown) {
      const errorDetails = getApiErrorDetails(error);
      notify('Error', errorDetails.message || 'Failed to change role', 'danger');
    } finally {
      setIsRoleChanging(false);
    }
  };

  const filtered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      u =>
        u.firstName.toLowerCase().includes(q) ||
        u.lastName.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q),
    );
  }, [data, debouncedQuery]);

  const migratedCount = useMemo(() => data.filter(u => u.workspaceId).length, [data]);
  const pendingCount = useMemo(() => data.filter(u => !u.workspaceId).length, [data]);

  const totalItems = filtered.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filtered.slice(startIndex, startIndex + itemsPerPage);
  }, [filtered, currentPage, itemsPerPage]);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);

      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 3) {
        start = 2;
        end = Math.min(totalPages - 1, maxPagesToShow - 1);
      }

      if (currentPage >= totalPages - 2) {
        start = Math.max(2, totalPages - (maxPagesToShow - 2));
        end = totalPages - 1;
      }

      if (start > 2) {
        pageNumbers.push("ellipsis1");
      }

      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }

      if (end < totalPages - 1) {
        pageNumbers.push("ellipsis2");
      }

      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
  };


  if (loading) {
    return <LoadingSpinner label="Loading users..." />;
  }

  return (
    <TooltipProvider>
      <div className="w-full space-y-4">
        {/* Toolbar: stats + search */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">All Users</h2>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="font-normal">
                {data.length} total
              </Badge>
              {migratedCount > 0 && (
                <Badge variant="success" className="font-normal">
                  {migratedCount} migrated
                </Badge>
              )}
              {pendingCount > 0 && (
                <Badge variant="warning" className="font-normal">
                  {pendingCount} pending
                </Badge>
              )}
            </div>
          </div>
          <div className="relative w-full sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search by name or email..."
              className="pl-9 pr-8"
              aria-label="Search users"
            />
            {query && (
              <button
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                onClick={handleClearSearch}
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Results info */}
        <div className="text-xs text-muted-foreground">
          {debouncedQuery ?
            `Showing ${totalItems === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1}\u2013${Math.min(currentPage * itemsPerPage, totalItems)} of ${totalItems} results for \u201c${debouncedQuery}\u201d` :
            `Showing ${totalItems === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1}\u2013${Math.min(currentPage * itemsPerPage, totalItems)} of ${totalItems} users`
          }
        </div>

        {filtered.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed p-12 text-center">
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <UserX className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium">No users found</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {debouncedQuery
                    ? `No results matching \u201c${debouncedQuery}\u201d`
                    : 'No users have been added yet'}
                </p>
              </div>
              {debouncedQuery && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearSearch}
                  className="mt-1 gap-2"
                >
                  <X className="h-3.5 w-3.5" />
                  Clear search
                </Button>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent bg-muted/50">
                    <TableHead className="text-xs font-medium">ID</TableHead>
                    <TableHead className="text-xs font-medium">First Name</TableHead>
                    <TableHead className="text-xs font-medium">Last Name</TableHead>
                    <TableHead className="text-xs font-medium">Email</TableHead>
                    <TableHead className="text-xs font-medium">Role</TableHead>
                    <TableHead className="text-xs font-medium hidden md:table-cell">Workspace ID</TableHead>
                    <TableHead className="text-xs font-medium hidden lg:table-cell">Organization</TableHead>
                    <TableHead className="text-xs font-medium">Status</TableHead>
                    <TableHead className="text-xs font-medium text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedData.map((user) => (
                    <TableRow key={user.id} className="group transition-colors">
                      <TableCell className="font-mono text-xs">
                        <div className="flex items-center gap-1.5">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-block cursor-default">{truncateId(user.id)}</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="font-mono text-xs">{user.id}</p>
                            </TooltipContent>
                          </Tooltip>
                          <button
                            onClick={() => handleCopyId(user.id)}
                            className={`opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded ${copiedId === user.id ? 'text-primary' : 'text-muted-foreground hover:text-primary'}`}
                            aria-label="Copy ID"
                          >
                            {copiedId === user.id ? <Check size={12} /> : <Copy size={12} />}
                          </button>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{user.firstName}</TableCell>
                      <TableCell className="text-sm">{user.lastName}</TableCell>
                      <TableCell className="text-sm">{user.email}</TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className="text-[11px] font-normal">
                            {user.role || 'User'}
                          </Badge>
                          {user.workspaceId && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => handleOpenRoleDialog(user)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded text-muted-foreground hover:text-primary"
                                  aria-label="Change role"
                                >
                                  <Pencil size={12} />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>Change role</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell font-mono text-xs">
                        {user.workspaceId ? (
                          <div className="flex items-center gap-1.5">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-block cursor-default">{truncateId(user.workspaceId)}</span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="font-mono text-xs">{user.workspaceId}</p>
                              </TooltipContent>
                            </Tooltip>
                            <button
                              onClick={() => user.workspaceId && handleCopyId(user.workspaceId)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded text-muted-foreground hover:text-primary"
                              aria-label="Copy Workspace ID"
                            >
                              <Copy size={12} />
                            </button>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">&mdash;</span>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-sm">
                        {user.organization || <span className="text-muted-foreground">&mdash;</span>}
                      </TableCell>
                      <TableCell>
                        {user.workspaceId ? (
                          <Badge variant="success" className="text-[11px] font-normal">Migrated</Badge>
                        ) : (
                          <Badge variant="warning" className="text-[11px] font-normal">Pending</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {!user.workspaceId && (
                          <div className="flex gap-1.5 justify-end">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 gap-1.5 text-xs"
                                  onClick={() => handleMigrate(user)}
                                >
                                  <ArrowRightLeft className="h-3.5 w-3.5" />
                                  Migrate
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Migrate user to Access Workspace</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                  onClick={() => {
                                    setUserToDelete(user);
                                    setIsDeleteDialogOpen(true);
                                  }}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Delete user</TooltipContent>
                            </Tooltip>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Label htmlFor="itemsPerPage" className="text-xs">Rows</Label>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={handleItemsPerPageChange}
                >
                  <SelectTrigger id="itemsPerPage" className="w-[70px] h-8 text-xs">
                    <SelectValue placeholder="25" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-xs">per page</span>
              </div>

              {totalPages > 1 && (
                <Pagination className="mt-0">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (currentPage > 1) handlePageChange(currentPage - 1);
                        }}
                        aria-disabled={currentPage === 1}
                        className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                      />
                    </PaginationItem>

                    {getPageNumbers().map((page, i) => (
                      typeof page === "string" ? (
                        <PaginationItem key={`ellipsis-${i}`}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      ) : (
                        <PaginationItem key={page}>
                          <PaginationLink
                            href="#"
                            isActive={page === currentPage}
                            onClick={(e) => {
                              e.preventDefault();
                              handlePageChange(page);
                            }}
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      )
                    ))}

                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (currentPage < totalPages) handlePageChange(currentPage + 1);
                        }}
                        aria-disabled={currentPage === totalPages}
                        className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </div>
          </>
        )}

        {/* Migration Dialog */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Migrate User</DialogTitle>
              <DialogDescription>
                Select a subscription to migrate{' '}
                <span className="font-medium text-foreground">
                  {selectedUser?.firstName} {selectedUser?.lastName}
                </span>{' '}
                to Access Workspace.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label className="text-sm mb-2 block">Subscription</Label>
              <Select value={selectedSubscription || ''} onValueChange={setSelectedSubscription}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select subscription" />
                </SelectTrigger>
                <SelectContent>
                  {subscriptionOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleMigrateConfirm} className="gap-2">
                <ArrowRightLeft className="h-4 w-4" />
                Confirm Migration
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete User</DialogTitle>
              <DialogDescription>
                This action cannot be undone. The user will be permanently removed.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 rounded-lg bg-muted/50 px-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Name</span>
                <span className="font-medium">{userToDelete?.firstName} {userToDelete?.lastName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Email</span>
                <span className="font-medium">{userToDelete?.email}</span>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)} disabled={isDeleting}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={isDeleting}
                className="gap-2"
                onClick={async () => {
                  if (userToDelete) {
                    setIsDeleting(true);
                    try {
                      await handleDelete(userToDelete.id);
                    } finally {
                      setIsDeleting(false);
                    }
                    setIsDeleteDialogOpen(false);
                    setUserToDelete(null);
                  }
                }}
              >
                <Trash2 className="h-4 w-4" />
                {isDeleting ? 'Deleting...' : 'Delete User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Role Change Dialog */}
        <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change Role</DialogTitle>
              <DialogDescription>
                Change the role for{' '}
                <span className="font-medium text-foreground">
                  {roleChangeUser?.firstName} {roleChangeUser?.lastName}
                </span>.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="rounded-lg bg-muted/50 px-4 py-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Name</span>
                  <span className="font-medium">{roleChangeUser?.firstName} {roleChangeUser?.lastName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Email</span>
                  <span className="font-medium">{roleChangeUser?.email}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Current Role</span>
                  <Badge variant="outline" className="text-[11px] font-normal">
                    {roleChangeUser?.role || 'User'}
                  </Badge>
                </div>
              </div>
              <div>
                <Label className="text-sm mb-2 block">New Role</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_ROLES.map((role) => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsRoleDialogOpen(false)} disabled={isRoleChanging}>
                Cancel
              </Button>
              <Button
                onClick={handleRoleChangeConfirm}
                disabled={isRoleChanging || selectedRole === (roleChangeUser?.role || 'User')}
                className="gap-2"
              >
                {isRoleChanging ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                {isRoleChanging ? 'Changing...' : 'Confirm'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default UsersTable;
